/**
 * Write a run's answer, unless a newer answer has already been written or a
 * reset() happened since the run started. Returns whether it wrote.
 *
 * `verdict: false` marks a non-answer (a network failure, a 500, a 401): it is
 * written under the same rule but does not count as written, so an OLDER run's
 * real answer that lands afterwards still gets through.
 */
export type Apply = (write: () => void, opts?: { verdict?: boolean }) => boolean;

/**
 * Wrap `run` so it executes at most once per key: concurrent calls with the
 * same key share one in-flight run (and its result), and a key whose run
 * succeeded is not run again. A new key runs, `{ force: true }` always runs, and a failed run is
 * forgotten so the next call retries. `reset()` forgets the remembered key
 * and drops the writes of every run started before it.
 *
 * Writes go through `apply` (see Apply): latest APPLIED answer wins. "Only the
 * newest run may write" is wrong here — on a page load the stored token's
 * check is usually overtaken by a TOKEN_REFRESHED check, and skipping the
 * first answer left the page with no user, which bounces to /login.
 *
 * Used to ask GET /api/auth/admin/me once per access token: on every page
 * load auth-js reports the same session three times (getSession, the SIGNED_IN
 * its session recovery emits, and INITIAL_SESSION).
 */
export function singleFlightByKey<T = void>(
  run: (key: string, apply: Apply) => Promise<T>,
): ((key: string, opts?: { force?: boolean }) => Promise<T>) & { reset: () => void } {
  type Entry = { key: string; promise: Promise<T> };
  let last: Entry | null = null;
  let started = 0; // sequence number of the most recently started run
  let appliedSeq = 0; // sequence number of the newest run whose verdict was written
  let generation = 0; // bumped by reset()

  const call = (key: string, opts?: { force?: boolean }): Promise<T> => {
    if (!opts?.force && last?.key === key) return last.promise;
    const seq = ++started;
    const gen = generation;
    const apply: Apply = (write, applyOpts) => {
      if (gen !== generation || seq < appliedSeq) return false;
      if (applyOpts?.verdict !== false) appliedSeq = seq;
      write();
      return true;
    };
    const entry: Entry = {
      key,
      // Deferred a microtask so a synchronous throw from run becomes a
      // rejection (and is forgotten) instead of escaping past `last`.
      promise: Promise.resolve()
        .then(() => run(key, apply))
        .catch((err: unknown) => {
          // Only forget our own entry: a newer key may already have replaced it.
          if (last === entry) last = null;
          throw err;
        }),
    };
    last = entry;
    return entry.promise;
  };

  return Object.assign(call, {
    reset: () => {
      last = null;
      generation++;
    },
  });
}
